/**
 * The one resend rule for an unpaid deposit link (W5b #2, tv-2na.8.20 / 8.19;
 * DECIDED 25 Sep Q1/Q2). Pure: the web surfaces use it to decide whether to
 * offer "Resend link", and POST /api/intents/resend-link re-runs it under the
 * deposit lock before minting anything, so the button and the server can never
 * disagree.
 *
 * - same-slot:       mint a fresh link for the SAME slot.
 * - pick-new-date:   the slot has passed or been taken (Q1), or the old link
 *                    never had one (7g3y: a link always carries a date). Nothing is minted;
 *                    the caller opens "Pick a new date" prefilled, and the old
 *                    row stays Expired.
 * - not-resendable:  never re-send money that is in or moved on (part-paid,
 *                    paid, used, rolled over, refunded, forfeited), a live link,
 *                    a link already re-sent, a pay-at-studio hold, or a link
 *                    expired more than RESEND_WINDOW_DAYS ago (Q2: after that
 *                    only the Sweeper's Expired rung re-offers it).
 */

/** Q2: a resend is offered for this many days after the link expired. */
export const RESEND_WINDOW_DAYS = 7;

const DAY_MS = 86_400_000;

/** The deposit fields the rule reads. The web `Deposit` and the db row fit. */
export interface ResendDeposit {
  status: string;
  expiresAt: string;
  amountAud: number;
  amountPaid?: number;
  ledger?: ReadonlyArray<{ kind: string; amountAud: number }>;
  supersededByDepositId?: string;
  payAtStudio?: boolean;
}

export interface ResendSlot {
  startIso: string;
  endIso: string;
}

export type NotResendableReason =
  | 'not-expired'
  | 'money-in'
  | 'moved-on'
  | 'already-resent'
  | 'pay-at-studio'
  | 'window-closed';

/** Why a resend needs a new date: the slot passed, was taken, or the old link never had one (7g3y). */
export type NewDateReason = 'past' | 'taken' | 'no-date';

export type ResendDecision =
  | { kind: 'same-slot' }
  | { kind: 'pick-new-date'; reason: NewDateReason }
  | { kind: 'not-resendable'; reason: NotResendableReason };

/** Who is resending: the Sweeper's Expired rung may re-offer past the window. */
export type ResendVia = 'sweeper';

export interface ResendDecisionInput {
  deposit: ResendDeposit;
  /** The slot the old link held, if it held one. */
  slot?: ResendSlot;
  /** A live booking (not the link's own released hold) already overlaps `slot`. */
  slotTaken: boolean;
  nowIso: string;
  via?: ResendVia;
}

const MOVED_ON = new Set(['paid', 'rolled-over', 'refunded', 'forfeited']);

/** Money in on the deposit: the ledger net (paid-like minus out-like), else amountPaid. */
function moneyIn(d: ResendDeposit): number {
  if (d.ledger && d.ledger.length > 0) {
    return d.ledger.reduce((sum, e) => {
      if (e.kind === 'paid' || e.kind === 'topup' || e.kind === 'rollover-in' || e.kind === 'reinstate') {
        return sum + Number(e.amountAud);
      }
      if (e.kind === 'rollover-out' || e.kind === 'forfeit') return sum - Number(e.amountAud);
      return sum;
    }, 0);
  }
  return Number(d.amountPaid ?? 0);
}

/** Why a link can never be re-sent, independent of its slot; undefined = it can. */
function blocker(d: ResendDeposit, nowMs: number, via?: ResendVia): NotResendableReason | undefined {
  if (MOVED_ON.has(d.status)) return 'moved-on';
  if (d.ledger?.some((e) => e.kind === 'redeemed')) return 'moved-on';
  if (moneyIn(d) > 0 || Number(d.amountPaid ?? 0) > 0) return 'money-in';
  if (d.supersededByDepositId) return 'already-resent';
  if (d.payAtStudio) return 'pay-at-studio';
  const expiresMs = Date.parse(d.expiresAt);
  const expired = d.status === 'expired' || (d.status === 'pending' && expiresMs < nowMs);
  if (!expired) return 'not-expired';
  if (via !== 'sweeper' && nowMs - expiresMs > RESEND_WINDOW_DAYS * DAY_MS) return 'window-closed';
  return undefined;
}

export function resendDecision(input: ResendDecisionInput): ResendDecision {
  const nowMs = Date.parse(input.nowIso);
  const reason = blocker(input.deposit, nowMs, input.via);
  if (reason) return { kind: 'not-resendable', reason };
  // A deposit link always carries a date (diagram 03; Anas 1 Oct, 7g3y): an
  // older dateless link is re-sent only with a date picked.
  if (!input.slot) return { kind: 'pick-new-date', reason: 'no-date' };
  if (Date.parse(input.slot.startIso) <= nowMs) return { kind: 'pick-new-date', reason: 'past' };
  if (input.slotTaken) return { kind: 'pick-new-date', reason: 'taken' };
  return { kind: 'same-slot' };
}

/**
 * Whether a surface should offer "Resend link" at all. Slot-independent: a
 * past or taken slot is still offered and resolves to "Pick a new date".
 */
export function isResendOffered(deposit: ResendDeposit, nowIso: string, opts: { via?: ResendVia } = {}): boolean {
  return blocker(deposit, Date.parse(nowIso), opts.via) === undefined;
}
